export type LearnerFieldSaveState = 'queued' | 'saving' | 'saved' | 'failed';

export interface LearnerFieldSaveCommitError {
  kind: 'network' | 'http' | 'unknown';
  status?: number;
}

export interface LearnerFieldSaveMutation<T> {
  key: string;
  payload: T;
  mutationId: string;
}

export interface LearnerFieldSaveTicket<TResult> {
  mutationId: string;
  acknowledgement: Promise<TResult>;
}

export interface LearnerFieldSaveStateChange {
  key: string;
  mutationId: string;
  state: LearnerFieldSaveState;
  error?: LearnerFieldSaveCommitError;
}

export interface LearnerFieldSaveScheduler {
  setTimeout(callback: () => void, delayMs: number): unknown;
  clearTimeout(handle: unknown): void;
}

export interface LearnerFieldSaveCoordinatorDependencies<T, TResult> {
  commit(mutation: LearnerFieldSaveMutation<T>): Promise<TResult>;
  scheduler?: LearnerFieldSaveScheduler;
  now?: () => number;
  random?: () => number;
  createMutationId?: () => string;
  onStateChange?: (change: LearnerFieldSaveStateChange) => void;
}

export interface LearnerFieldSaveCoordinator<T, TResult> {
  enqueue(input: { key: string; payload: T }): LearnerFieldSaveTicket<TResult>;
  retryFailed(): number;
  getState(key: string): LearnerFieldSaveStateChange | undefined;
  hasPending(): boolean;
  reset(reason?: unknown): void;
}

type MutationPhase = 'committing' | 'retry-waiting' | 'retry-ready' | 'failed';

interface AcknowledgementWaiter<TResult> {
  resolve(value: TResult): void;
  reject(reason: LearnerFieldSaveCommitError): void;
}

interface PendingMutation<T, TResult> extends LearnerFieldSaveMutation<T> {
  automaticRetries: number;
  phase?: MutationPhase;
  failedAt?: number;
  retryTimer?: unknown;
  waiters: AcknowledgementWaiter<TResult>[];
}

interface Entry<T, TResult> {
  active?: PendingMutation<T, TResult>;
  queued?: PendingMutation<T, TResult>;
  quietReady: boolean;
  quietTimer?: unknown;
  lastState?: LearnerFieldSaveStateChange;
}

const QUIET_WINDOW_MS = 1_000;
const FAILED_RECOVERY_COOLDOWN_MS = 30_000;
const MAX_AUTOMATIC_ATTEMPTS = 3;
const RETRY_BASE_MS = 1_000;
const MAX_IN_FLIGHT_PER_TAB = 2;

const browserScheduler: LearnerFieldSaveScheduler = {
  setTimeout: (callback, delayMs) => globalThis.setTimeout(callback, delayMs),
  clearTimeout: (handle) => globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>),
};

function defaultMutationId() {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return `learner-field-save-${Date.now()}-${Math.random()}`;
}

function normalizeCommitError(error: unknown): LearnerFieldSaveCommitError {
  if (
    typeof error === 'object'
    && error !== null
    && ['network', 'http', 'unknown'].includes((error as { kind?: string }).kind ?? '')
  ) {
    const candidate = error as LearnerFieldSaveCommitError;
    return typeof candidate.status === 'number'
      ? { kind: candidate.kind, status: candidate.status }
      : { kind: candidate.kind };
  }
  return { kind: 'unknown' };
}

function isRetryable(error: LearnerFieldSaveCommitError) {
  return error.kind === 'network'
    || (error.kind === 'http' && [429, 502, 503, 504].includes(error.status ?? 0));
}

export function createLearnerFieldSaveCoordinator<T, TResult>(
  dependencies: LearnerFieldSaveCoordinatorDependencies<T, TResult>,
): LearnerFieldSaveCoordinator<T, TResult> {
  const scheduler = dependencies.scheduler ?? browserScheduler;
  const now = dependencies.now ?? (() => Date.now());
  const random = dependencies.random ?? Math.random;
  const createMutationId = dependencies.createMutationId ?? defaultMutationId;
  const entries = new Map<string, Entry<T, TResult>>();
  let inFlightCount = 0;

  const publish = (
    entry: Entry<T, TResult>,
    mutation: PendingMutation<T, TResult>,
    state: LearnerFieldSaveState,
    error?: LearnerFieldSaveCommitError,
  ) => {
    const change: LearnerFieldSaveStateChange = {
      key: mutation.key,
      mutationId: mutation.mutationId,
      state,
      ...(error ? { error } : {}),
    };
    entry.lastState = change;
    dependencies.onStateChange?.(change);
  };

  const drain = () => {
    while (inFlightCount < MAX_IN_FLIGHT_PER_TAB) {
      const retryEntry = [...entries.values()].find((entry) => entry.active?.phase === 'retry-ready');
      if (retryEntry?.active) {
        beginCommit(retryEntry, retryEntry.active);
        continue;
      }

      const queuedEntry = [...entries.values()].find(
        (entry) => entry.quietReady && !entry.active && entry.queued,
      );
      if (!queuedEntry?.queued) return;
      const mutation = queuedEntry.queued;
      queuedEntry.queued = undefined;
      queuedEntry.quietReady = false;
      queuedEntry.active = mutation;
      beginCommit(queuedEntry, mutation);
    }
  };

  const resolveAcknowledgements = (mutation: PendingMutation<T, TResult>, result: TResult) => {
    for (const waiter of mutation.waiters) waiter.resolve(result);
    mutation.waiters = [];
  };

  const rejectAcknowledgements = (
    mutation: PendingMutation<T, TResult>,
    error: LearnerFieldSaveCommitError,
  ) => {
    for (const waiter of mutation.waiters) waiter.reject(error);
    mutation.waiters = [];
  };

  const beginCommit = (entry: Entry<T, TResult>, mutation: PendingMutation<T, TResult>) => {
    if (entry.active !== mutation || inFlightCount >= MAX_IN_FLIGHT_PER_TAB) return;
    mutation.phase = 'committing';
    inFlightCount += 1;
    publish(entry, mutation, 'saving');

    void Promise.resolve()
      .then(() => dependencies.commit(mutation))
      .then((result) => {
        inFlightCount -= 1;
        if (entry.active !== mutation) {
          drain();
          return;
        }
        entry.active = undefined;
        publish(entry, mutation, 'saved');
        resolveAcknowledgements(mutation, result);
        drain();
      })
      .catch((reason: unknown) => {
        inFlightCount -= 1;
        if (entry.active !== mutation) {
          drain();
          return;
        }

        const error = normalizeCommitError(reason);
        if (
          isRetryable(error)
          && mutation.automaticRetries + 1 < MAX_AUTOMATIC_ATTEMPTS
        ) {
          const delayMs = random() * RETRY_BASE_MS * (2 ** mutation.automaticRetries);
          mutation.automaticRetries += 1;
          mutation.phase = 'retry-waiting';
          mutation.retryTimer = scheduler.setTimeout(() => {
            if (entry.active !== mutation || mutation.phase !== 'retry-waiting') return;
            mutation.retryTimer = undefined;
            mutation.phase = 'retry-ready';
            drain();
          }, delayMs);
          drain();
          return;
        }

        publish(entry, mutation, 'failed', error);
        rejectAcknowledgements(mutation, error);
        if (entry.queued) {
          entry.active = undefined;
          drain();
          return;
        }
        mutation.phase = 'failed';
        mutation.failedAt = now();
        drain();
      });
  };

  const scheduleQuietWindow = (entry: Entry<T, TResult>, mutation: PendingMutation<T, TResult>) => {
    if (entry.quietTimer !== undefined) scheduler.clearTimeout(entry.quietTimer);
    entry.quietReady = false;
    entry.quietTimer = scheduler.setTimeout(() => {
      if (entry.queued !== mutation) return;
      entry.quietTimer = undefined;
      entry.quietReady = true;
      drain();
    }, QUIET_WINDOW_MS);
  };

  return {
    enqueue({ key, payload }) {
      const entry = entries.get(key) ?? { quietReady: false };
      entries.set(key, entry);
      const mutation: PendingMutation<T, TResult> = {
        key,
        payload,
        mutationId: createMutationId(),
        automaticRetries: 0,
        waiters: [],
      };

      const acknowledgement = new Promise<TResult>((resolve, reject) => {
        mutation.waiters.push({ resolve, reject });
      });
      void acknowledgement.catch(() => undefined);

      if (entry.active?.phase === 'failed') {
        entry.active = undefined;
      }
      if (entry.queued) {
        mutation.waiters.push(...entry.queued.waiters);
        entry.queued.waiters = [];
      }
      entry.queued = mutation;
      publish(entry, mutation, 'queued');
      scheduleQuietWindow(entry, mutation);
      return { mutationId: mutation.mutationId, acknowledgement };
    },

    retryFailed() {
      let claimed = 0;
      for (const entry of entries.values()) {
        const mutation = entry.active;
        if (
          mutation?.phase !== 'failed'
          || mutation.failedAt === undefined
          || now() - mutation.failedAt < FAILED_RECOVERY_COOLDOWN_MS
        ) {
          continue;
        }
        mutation.phase = 'retry-ready';
        mutation.failedAt = undefined;
        publish(entry, mutation, 'queued');
        claimed += 1;
      }
      drain();
      return claimed;
    },

    getState(key) {
      const entry = entries.get(key);
      if (!entry) return undefined;
      if (entry.queued) {
        return { key, mutationId: entry.queued.mutationId, state: 'queued' };
      }
      if (entry.active) {
        return {
          key,
          mutationId: entry.active.mutationId,
          state: entry.active.phase === 'failed' ? 'failed' : 'saving',
          ...(entry.active.phase === 'failed' && entry.lastState?.error
            ? { error: entry.lastState.error }
            : {}),
        };
      }
      return entry.lastState;
    },

    hasPending() {
      return [...entries.values()].some((entry) => entry.queued || entry.active);
    },

    reset(reason?: unknown) {
      const error = normalizeCommitError(reason);
      for (const entry of entries.values()) {
        if (entry.quietTimer !== undefined) {
          scheduler.clearTimeout(entry.quietTimer);
          entry.quietTimer = undefined;
        }
        if (entry.active?.retryTimer !== undefined) {
          scheduler.clearTimeout(entry.active.retryTimer);
          entry.active.retryTimer = undefined;
        }
        if (entry.active) rejectAcknowledgements(entry.active, error);
        if (entry.queued) rejectAcknowledgements(entry.queued, error);
        entry.active = undefined;
        entry.queued = undefined;
      }
      entries.clear();
    },
  };
}
